import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  School, Building, User, Users, Book, FileText, 
  Star, Loader, AlertCircle, Plus, X, Trash2, AlertTriangle
} from 'lucide-react';
import toast from 'react-hot-toast';
import AuthContext from '../../contexts/AuthContext';

/**
 * Moderator School Details Page component
 * Shows detailed information about the moderator's school
 */
const ModeratorSchoolDetailsPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [school, setSchool] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [stats, setStats] = useState({
    users: { total: 0, verified: 0 },
    academic: { departments: 0, courses: 0, sections: 0 },
    materials: { total: 0, notes: 0, exams: 0, syllabi: 0, other: 0, reported: 0 },
    professors: { total: 0 },
    ratings: { total: 0, average: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Department creation states
  const [showAddDepartment, setShowAddDepartment] = useState(false);
  const [newDepartment, setNewDepartment] = useState({ name: '', code: '' });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [deleteDepartmentConfirmation, setDeleteDepartmentConfirmation] = useState(null);
  const [isDeletingDepartment, setIsDeletingDepartment] = useState(false);
  
  useEffect(() => {
    // Redirect if user is not moderator
    if (user && user.role !== 'moderator') {
      toast.error('You do not have permission to access this page');
      navigate('/school');
      return;
    }
    
    // Fetch data if user is moderator
    if (user && user.role === 'moderator' && user.school?.id) {
      fetchSchoolData();
    }
  }, [user, navigate]);
  
  const fetchSchoolData = async () => {
    try {
      setLoading(true);
      
      // Get school ID from the auth context
      const schoolId = user.school?.id;
      
      if (!schoolId) {
        throw new Error('School information not found');
      }
      
      // Fetch school details
      const schoolRes = await fetch(
        `${process.env.REACT_APP_API_URL}/api/schools/${schoolId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!schoolRes.ok) {
        throw new Error('Failed to fetch school details');
      }
      
      const schoolData = await schoolRes.json();
      setSchool(schoolData);
      
      if (schoolData.departments) {
        setDepartments(schoolData.departments);
      }
      
      // Fetch school stats
      try {
        const statsRes = await fetch(
          `${process.env.REACT_APP_API_URL}/api/schools/${schoolId}/stats`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        if (statsRes.ok) {
          const statsData = await statsRes.json();
          setStats(statsData);
        } else {
          console.error('Failed to fetch school statistics');
          // Continue with default stats values
        }
      } catch (statsError) {
        console.error('Error fetching school statistics:', statsError);
        // Continue with default stats values
      }
      
    } catch (error) {
      console.error('Error fetching school data:', error);
      setError(error.message);
      toast.error('Failed to load school data');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle input changes for new department form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewDepartment({
      ...newDepartment,
      [name]: value
    });
    
    // Clear error for this field if it exists
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };
  
  // Validate the department form
  const validateForm = () => {
    const errors = {};
    
    if (!newDepartment.name.trim()) {
      errors.name = 'Department name is required';
    }
    
    if (!newDepartment.code.trim()) {
      errors.code = 'Department code is required';
    } else if (!/^[A-Z0-9]{2,10}$/.test(newDepartment.code.trim())) {
      errors.code = 'Code must be 2-10 uppercase letters or numbers';
    }
    
    // Check if department code already exists
    const codeExists = departments.some(
      dept => dept.code.toLowerCase() === newDepartment.code.trim().toLowerCase()
    );
    
    if (codeExists) {
      errors.code = 'A department with this code already exists';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Handle department form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const schoolId = user.school?.id;
      
      if (!schoolId) {
        throw new Error('School information not found');
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/departments`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            schoolId,
            name: newDepartment.name.trim(),
            code: newDepartment.code.trim().toUpperCase()
          })
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create department');
      }
      
      const createdDepartment = await response.json();
      
      // Update departments list with the new department
      setDepartments([...departments, createdDepartment]);
      
      // Update stats if they exist
      if (stats.academic) {
        setStats({
          ...stats,
          academic: {
            ...stats.academic,
            departments: (stats.academic.departments || 0) + 1
          }
        });
      }
      
      // Close the modal and reset form
      setShowAddDepartment(false);
      setNewDepartment({ name: '', code: '' });
      
      toast.success('Department created successfully');
    } catch (error) {
      console.error('Error creating department:', error);
      toast.error(error.message || 'Failed to create department');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle department deletion
  const handleDeleteDepartment = async (departmentId) => {
    try {
      setIsDeletingDepartment(true);
      
      const schoolId = user.school?.id;
      
      if (!schoolId) {
        throw new Error('School information not found');
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/departments/${departmentId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete department');
      }
      
      // Remove the deleted department from state
      setDepartments(departments.filter(dept => dept.id !== departmentId));
      
      // Update stats if they exist
      if (stats.academic) {
        setStats({
          ...stats,
          academic: {
            ...stats.academic,
            departments: Math.max(0, (stats.academic.departments || 1) - 1)
          }
        });
      }
      
      // Close confirmation dialog
      setDeleteDepartmentConfirmation(null);
      
      toast.success('Department deleted successfully');
    } catch (error) {
      console.error('Error deleting department:', error);
      toast.error(error.message || 'Failed to delete department');
    } finally {
      setIsDeletingDepartment(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader className="h-8 w-8 text-blue-500 animate-spin mr-2" />
        <span className="text-gray-600 dark:text-gray-400">Loading school data...</span>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Error Loading School Data
        </h2>
        <p className="text-gray-600 dark:text-gray-400 text-center max-w-md">
          {error}
        </p>
      </div>
    );
  }
  
  if (!school) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="h-12 w-12 text-yellow-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
          School Not Found
        </h2>
        <p className="text-gray-600 dark:text-gray-400 text-center max-w-md">
          Could not find information for your school.
        </p>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <div className="flex items-center">
          <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 mr-4">
            <School size={24} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {school.name}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {school.domain}
            </p>
          </div>
        </div>
      </div>
      
      {/* School Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Users</p>
              <h3 className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                {stats.users?.total || 0}
              </h3>
            </div>
            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
              <Users size={20} />
            </div>
          </div>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {stats.users?.verified || 0} verified users
          </p>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Courses</p>
              <h3 className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                {stats.academic?.courses || 0}
              </h3>
            </div>
            <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
              <Book size={20} />
            </div>
          </div>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {stats.academic?.sections || 0} active sections
          </p>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Professors</p>
              <h3 className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                {stats.professors?.total || 0}
              </h3>
            </div>
            <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
              <User size={20} />
            </div>
          </div>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {stats.ratings?.total || 0} ratings submitted
          </p>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Materials</p>
              <h3 className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                {stats.materials?.total || 0}
              </h3>
            </div>
            <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
              <FileText size={20} />
            </div>
          </div>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {stats.materials?.reported || 0} reported items
          </p>
        </div>
      </div>
      
      {/* Departments */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Departments
          </h2>
          <button
            onClick={() => setShowAddDepartment(true)}
            className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Department
          </button>
        </div>
        
        {departments && departments.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {departments.map((department) => (
                <div key={department.id} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                    <div className="flex items-center">
                        <Building className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2" />
                        <div>
                        <h3 className="font-medium text-gray-900 dark:text-white">
                            {department.name}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            Code: {department.code}
                        </p>
                        </div>
                    </div>
                    <button
                        onClick={() => setDeleteDepartmentConfirmation(department)}
                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                        title="Delete department"
                    >
                        <Trash2 size={18} />
                    </button>
                    </div>
                </div>
                ))}
            </div>
            ) : (
          <p className="text-gray-600 dark:text-gray-400">
            No departments found for this school. Add your first department to get started.
          </p>
        )}
      </div>
      
      {/* Material Stats Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        {/* ... Material stats content remains the same ... */}
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Material Distribution
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-300">Notes</span>
              <span className="font-semibold text-blue-600 dark:text-blue-400">
                {stats.materials?.notes || 0}
              </span>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-300">Exams</span>
              <span className="font-semibold text-red-600 dark:text-red-400">
                {stats.materials?.exams || 0}
              </span>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-300">Syllabi</span>
              <span className="font-semibold text-green-600 dark:text-green-400">
                {stats.materials?.syllabi || 0}
              </span>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-300">Other</span>
              <span className="font-semibold text-purple-600 dark:text-purple-400">
                {stats.materials?.other || 0}
              </span>
            </div>
          </div>
        </div>
        
        {/* Visual representation of material distribution */}
        {stats.materials?.total > 0 && (
          <div className="mt-4">
            <div className="relative pt-1">
              <div className="flex h-4 overflow-hidden text-xs bg-gray-200 dark:bg-gray-600 rounded-full">
                <div 
                  className="flex flex-col justify-center text-center text-white bg-blue-500" 
                  style={{ width: `${(stats.materials.notes / stats.materials.total) * 100}%` }}
                ></div>
                <div 
                  className="flex flex-col justify-center text-center text-white bg-red-500" 
                  style={{ width: `${(stats.materials.exams / stats.materials.total) * 100}%` }}
                ></div>
                <div 
                  className="flex flex-col justify-center text-center text-white bg-green-500" 
                  style={{ width: `${(stats.materials.syllabi / stats.materials.total) * 100}%` }}
                ></div>
                <div 
                  className="flex flex-col justify-center text-center text-white bg-purple-500" 
                  style={{ width: `${(stats.materials.other / stats.materials.total) * 100}%` }}
                ></div>
              </div>
            </div>
            
            <div className="flex text-xs mt-2 justify-between text-gray-600 dark:text-gray-400">
              <span className="flex items-center">
                <span className="w-2 h-2 inline-block mr-1 bg-blue-500 rounded-full"></span>
                Notes
              </span>
              <span className="flex items-center">
                <span className="w-2 h-2 inline-block mr-1 bg-red-500 rounded-full"></span>
                Exams
              </span>
              <span className="flex items-center">
                <span className="w-2 h-2 inline-block mr-1 bg-green-500 rounded-full"></span>
                Syllabi
              </span>
              <span className="flex items-center">
                <span className="w-2 h-2 inline-block mr-1 bg-purple-500 rounded-full"></span>
                Other
              </span>
            </div>
          </div>
        )}
      </div>
      
      {/* Add Department Modal */}
      {showAddDepartment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Add New Department
              </h3>
              <button
                onClick={() => setShowAddDepartment(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Department Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="e.g. Computer Science"
                  value={newDepartment.name}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border ${
                    formErrors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-700'
                  } rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white`}
                />
                {formErrors.name && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.name}</p>
                )}
              </div>
              
              <div className="mb-6">
                <label htmlFor="code" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Department Code
                </label>
                <input
                  id="code"
                  name="code"
                  type="text"
                  placeholder="e.g. CS"
                  value={newDepartment.code}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border ${
                    formErrors.code ? 'border-red-500' : 'border-gray-300 dark:border-gray-700'
                  } rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white`}
                />
                {formErrors.code && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.code}</p>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Department code should be 2-10 characters (letters and numbers only). This will be displayed on course listings.
                </p>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddDepartment(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <Loader className="h-4 w-4 animate-spin mr-2" />
                      Creating...
                    </span>
                  ) : 'Create Department'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Department Confirmation Modal */}
      {deleteDepartmentConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center text-red-600 dark:text-red-400 mb-4">
              <AlertTriangle className="h-6 w-6 mr-2" />
              <h3 className="text-lg font-medium">Confirm Deletion</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete the department <strong>{deleteDepartmentConfirmation.name} ({deleteDepartmentConfirmation.code})</strong>? This action cannot be undone and will remove all associated courses and materials.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteDepartmentConfirmation(null)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                disabled={isDeletingDepartment}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteDepartment(deleteDepartmentConfirmation.id)}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                disabled={isDeletingDepartment}
              >
                {isDeletingDepartment ? (
                  <span className="flex items-center">
                    <Loader className="h-4 w-4 animate-spin mr-2" />
                    Deleting...
                  </span>
                ) : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModeratorSchoolDetailsPage;